import React, { useContext, useEffect, useState } from "react";
import AppContext from "../../context/AppContext";
import translations from "../../components/common/Translations";
import { Trash2 } from "lucide-react";
import { toast } from "../../utils/toast";
import CommonButton from "../../components/common/CommonButton";
import ModalCloseButton from "../../components/common/ModalCloseButton";
import ActionIconButton from "../../components/common/ActionIconButton";
import { isRTL } from "../../utils/rtl";

const translation = {
  en: {
    validations: "Validations",
    code: "Validation Code",
    msg: "Error Message",
    update: "Update",
    cancel: "Close",
  },
  ar: {
    validations: "التحققات",
    code: "رمز التحقق",
    msg: "رسالة خطأ",
    update: "تحديث",
    cancel: "إغلاق",
  },
};

const ViewValidationModal = ({
  previewKey,
  preview,
  setViewValidationModal,
  viewValidationModal,
  currentValidation,
  setCurrentValidation,
  validationId,
  setValidationFormValues,
  validationFormValues,
  updateValidationField,
  handleValidationDelete,
  droppedItems,
}) => {
  const { theme, langMode } = useContext(AppContext);
  const [validations, setValidations] = useState([]);

  useEffect(() => {
    // 1️⃣ Get validation values from droppedItems first (more reliable)
    const droppedItem = droppedItems.find(
      (item) => item.uniqueKey === previewKey
    );
    if (droppedItem && droppedItem.validationValues?.length > 0) {
      setValidations(droppedItem.validationValues);
    }
    // 2️⃣ Fallback to preview.validationValues (if no dropped item found)
    else if (preview?.validationValues?.length > 0) {
      setValidations(preview.validationValues);
    }
    // 3️⃣ If no existing validations
    else {
      setValidations([]);
    }
  }, [previewKey, preview, droppedItems]);

  console.log('validations',validations)
  const onCloseValidation = () => {
    setViewValidationModal(false);
  };

  const handleValidationUpdate = (validationUniqueKey, field, value) => {
    const updatedValidations = validations.map((validation) =>
      validation.validationId === validationUniqueKey

        ? { ...validation, [field]: value }
        : validation
    );

    setValidations(updatedValidations);
    setCurrentValidation((prev) => ({
      ...prev,
      [field]: value
    }));

  };

  const handleSaveChanges = () => {
    updateValidationField(validations, previewKey);
    toast.success(translations[langMode].validationupdated);
    setViewValidationModal(false);
  };

const handleDelete = (validation) => {
  handleValidationDelete(validation.validationId, previewKey, validation.id);

  const updatedValidations = validations.filter(
    (item) => item.validationId !== validation.validationId
  );

  setValidations(updatedValidations);
  toast.success(translations[langMode].validationremoved);
};

  return (
    viewValidationModal && (
      <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
        <div
          dir={isRTL(langMode) ? "rtl" : "ltr"}
          className={`bg-white rounded-lg p-6 relative max-w-lg w-full overflow-y-auto shadow-md ${theme === "dark"
            ? "dark-mode border"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
        >
          <ModalCloseButton
            className={`absolute top-2 ${isRTL(langMode) ? "left-2" : "right-2"}`}
            onClick={onCloseValidation}
          />
          <h3 className="text-lg font-bold mb-4">
            {translation[langMode]?.validations}
          </h3>

          {validations.length > 0 ? (
            <div className="space-y-3 h-[200px] overflow-y-auto">
              {validations.map((validation) => (
                <div
                  key={validation.validationId}
                  className="p-4 border rounded-lg bg-gray-50"
                >
                    <div className="mb-4">
                    <div className="flex justify-end mb-4">
                      <ActionIconButton
                        tone="red"
                        ariaLabel={translations[langMode].deletevalidation}
                        tooltipText={translations[langMode].deletevalidation}
                        tooltipPosition="left"
                        onClick={() => handleDelete(validation)}
                      >
                        <Trash2 size={20} />
                      </ActionIconButton>
                    </div>

                    <label
                      htmlFor={`validationCode-${validation.uniqueKey}`}
                      className="block text-sm font-medium text-gray-700"
                    >
                      {translation[langMode]?.code}
                    </label>
                    <input
                      id={`validationCode-${validation.validationId}`}
                      type="text"
                      value={validation.validationCode}
                      onChange={(e) =>
                        handleValidationUpdate(
                          validation.validationId,
                          "validationCode",
                          e.target.value
                        )
                      }
                      className="mt-1 w-full text-xs border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`errorMessage-${validation.uniqueKey}`}
                      className="block text-sm font-medium text-gray-700"
                    >
                      {translation[langMode]?.msg}
                    </label>
                    <input
                      id={`errorMessage-${validation.uniqueKey}`}
                      type="text"
                      value={validation.errorMessage}
                      onChange={(e) =>
                        handleValidationUpdate(
                          validation.validationId,
                          "errorMessage",
                          e.target.value
                        )
                      }
                      className="mt-1 w-full text-xs border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              {translations[langMode].novalidationavailable}
            </p>
          )}

          <div className="mt-6 flex justify-end gap-4">
            <CommonButton colorIntent="danger"
              type="button"
              variant="danger"
              onClick={onCloseValidation}
              tooltipText={translations[langMode].closewithoutsavingchnages}
              tooltipPosition="top"
            >
              {translation[langMode]?.cancel}
            </CommonButton>
            <CommonButton colorIntent="success"
              type="button"
              onClick={handleSaveChanges}
              variant="primary"
              tooltipText={translations[langMode].savechangesmadetovalidation}
              tooltipPosition="left"
            >
              {translation[langMode]?.update}
            </CommonButton>
          </div>
        </div>
      </div>
    )
  );
};

export default ViewValidationModal;
